angular
  .module('bb.appointments')
  .factory('AppointmentBookService', AppointmentBookService);

AppointmentBookService.$inject = ['$resource'];
function AppointmentBookService($resource) {
  const Appointment = $resource('/listing/:listingId/appointments/:action', {
    listingId: '@listingId',
  });

  const AppointmentBookingInfo = $resource(
    '/listing/:listingId/appointment_booking_info',
    {
      listingId: '@listingId',
    },
  );

  const AppointmentForCoop = $resource(
    '/user/coop/appointment/:appointmentId',
    {
      appointmentId: '@appointmentId',
    },
  );

  const Appointments = $resource('/listing/:listingId/appointments/confirmed', {
    listingId: '@listingId',
  });

  const NonAgent = $resource(
    '/nonagent/:id',
    { id: '@id' },
    {
      delete: { method: 'POST', url: '/nonagent/delete/:id' },
    },
  );

  const NonAgentApproval = $resource(
    '/nonagent/approve/:nonAgentApprovalHash',
    {
      nonAgentApprovalHash: '@nonAgentApprovalHash',
    },
  );

  const CustomAgent = $resource(
    '/custom_agent/:id',
    { id: '@id' },
    {
      delete: { method: 'POST', url: '/custom_agent/delete/:id' },
    },
  );

  const NonAgentDetailsSearch = $resource('/nonagent/search/details');

  const ExternalConfig = $resource(
    '/listing/:listingId/appointments/external_config',
  );

  const OfferDetails = $resource('/listing/:listingId/offer_details', {
    listingId: '@listingId',
  });

  const RecordAppointment = $resource(
    '/listing/:listingId/appointments/record',
    { listingId: '@listingId' },
  );

  const AppointmentTimeChange = $resource(
    '/appointments/:appointmentId/time_change',
    { appointmentId: '@appointmentId' },
  );

  const AppointmentDetails = $resource('/appointments/:appointmentId', {
    appointmentId: '@appointmentId',
  });

  const AppointmentConfigContact = $resource(
    '/appointments/:listingId/config/contacts/:contactId',
    {
      listingId: '@listingId',
      contactId: '@contactId',
    },
  );

  // RETS RESOURCES //
  const RetsAppointment = $resource(
    '/external/listing/:retsId/appointment/:action',
    {
      retsId: '@retsId',
    },
  );

  const RetsAppointmentBookingInfo = $resource(
    '/external/listing/:retsId/appointment_booking/:listingId',
    {
      retsId: '@retsId',
      listingId: '@listingId',
    },
  );

  const RetsConfig = $resource(
    '/external/listing/:retsId/appointment/external_config',
    {
      retsId: '@retsId',
    },
  );

  const ExternalOfferDetails = $resource('/external/:retsId/offer_details', {
    retsId: '@retsId',
  });

  // DASHBOARD //

  const DashAppointment = $resource(
    '/external/dash/:dashboardId/appointment/:action',
    {
      dashboardId: '@dashboardId',
    },
  );

  const DashAppointmentBookingInfo = $resource(
    '/external/dash/:dashboardId/appointment_booking/:listingId',
    {
      dashboardId: '@dashboardId',
      listingId: '@listingId',
    },
  );

  const ExternalAppointmentTimeChange = $resource(
    '/external/dash/:dashboardId/appointment/:appointmentId/time_change',
    {
      appointmentId: '@appointmentId',
      dashboardId: '@dashboardId',
    },
  );

  const AppointmentEmailDelivery = $resource(
    '/appointment/:appointmentId/delivery',
    { appointmentId: '@appointmentId' },
  );

  const AppointmentTextDelivery = $resource(
    '/appointment/:appointmentId/texts',
    { appointmentId: '@appointmentId' },
  );

  const AppointmentPushNotificationDelivery = $resource(
    '/appointment/:appointmentId/pushNotifications',
    { appointmentId: '@appointmentId' },
  );

  // Phone Number for self
  const PhoneNumber = $resource('/phone/:type/:userId');

  // /////////////////////
  // Resource Functions //
  // ////////////////////
  function getAppointmentInfo(listingId, action, date) {
    if (!date) return Appointment.get({ listingId, action }).$promise;
    return Appointment.get({ listingId, action, date }).$promise;
  }

  function getAppointmentBookingInfo(listingId) {
    return AppointmentBookingInfo.get({ listingId }).$promise;
  }

  function getAppointments(listingId) {
    return Appointments.query({ listingId }).$promise;
  }

  function getAppointmentAndListingForCoop(appointmentId) {
    return AppointmentForCoop.get({ appointmentId }).$promise;
  }

  function getExternalConfig(listingId) {
    return ExternalConfig.get({ listingId }).$promise;
  }

  function getOfferDetails(listingId) {
    return OfferDetails.get({ listingId }).$promise;
  }

  function recordAppointment(appointment) {
    return RecordAppointment.save(appointment).$promise;
  }

  function saveAppointment(appointment, timeRestrictionsOverridden) {
    return Appointment.save(
      timeRestrictionsOverridden ? { override: 'true' } : {},
      appointment,
    ).$promise;
  }

  function saveNonAgent(id, nonAgent) {
    return NonAgent.save({ id }, nonAgent).$promise;
  }

  function deleteNonAgent(id) {
    return NonAgent.delete({ id }).$promise;
  }

  function saveCustomAgent(id, customAgent) {
    return CustomAgent.save({ id }, customAgent).$promise;
  }

  function getCustomAgent(id) {
    return CustomAgent.get({ id }).$promise;
  }

  function deleteCustomAgent(id) {
    return CustomAgent.delete({ id }).$promise;
  }

  function changeTime(appointment, options) {
    const { date, time, timeEnd, actionTaken } = appointment;
    const saveRequest = {
      appointmentId: appointment.id,
      date,
      time,
      timeEnd,
      actionTaken,
    };
    if (options?.isListingContact) {
      saveRequest.listingContactTimeChange = true;
    } else {
      saveRequest.listingAgentTimeChange = true;
    }
    saveRequest.keepOriginalAppointment = options.keepOriginalAppointment;

    const { timeRestrictionsOverridden } = options;
    const queryParams = timeRestrictionsOverridden ? { override: 'true' } : {};
    return AppointmentTimeChange.save(queryParams, saveRequest).$promise;
  }

  function changeTimeExternal(appointment, dashboardId, options) {
    const {
      date,
      time,
      timeEnd,
      actionTaken,
      captchaV3Token,
      captchaResponse,
    } = appointment;
    const saveRequest = {
      appointmentId: appointment.id,
      date,
      time,
      timeEnd,
      actionTaken,
      dashboardId,
      captchaV3Token,
      captchaResponse,
    };
    if (options?.isListingContact) {
      saveRequest.listingContactTimeChange = true;
    } else {
      saveRequest.listingAgentTimeChange = true;
    }
    saveRequest.keepOriginalAppointment = options.keepOriginalAppointment;

    const { timeRestrictionsOverridden } = options;
    const queryParams = timeRestrictionsOverridden ? { override: 'true' } : {};

    return ExternalAppointmentTimeChange.save(queryParams, saveRequest)
      .$promise;
  }

  function getAppointmentDetails(appointmentId) {
    return AppointmentDetails.get({ appointmentId }).$promise;
  }

  function getAppointmentConfigContact(listingId, contactId) {
    return AppointmentConfigContact.get({ listingId, contactId }).$promise;
  }

  function getNonAgentDetails(nonagent) {
    return NonAgentDetailsSearch.get({ nonagent }).$promise;
  }

  // RETS //

  function getRetsAppointmentInfo(retsId, action, date) {
    if (!date) return RetsAppointment.get({ retsId, action }).$promise;
    return RetsAppointment.get({ retsId, action, date }).$promise;
  }

  function getRetsAppointmentBookingInfo(retsId, listingId, options = {}) {
    return RetsAppointmentBookingInfo.get({ retsId, listingId, ...options })
      .$promise;
  }

  function getExtOfferDetails(retsId) {
    return ExternalOfferDetails.get({ retsId }).$promise;
  }

  function getRetsConfig(retsId) {
    return RetsConfig.get({ retsId }).$promise;
  }

  function saveRetsAppointment(appointment) {
    return RetsAppointment.save(appointment).$promise;
  }

  function queryAppointmentEmailDelivery(appointmentId) {
    return AppointmentEmailDelivery.query({ appointmentId }).$promise;
  }

  function queryAppointmentTextDelivery(appointmentId) {
    return AppointmentTextDelivery.query({ appointmentId }).$promise;
  }

  function queryAppointmentPushNotificationDelivery(appointmentId) {
    return AppointmentPushNotificationDelivery.query({ appointmentId })
      .$promise;
  }

  // DASH //
  function getDashAppointmentInfo(dashboardId, action, listingId, date) {
    if (!date)
      return DashAppointment.get({ dashboardId, action, listingId }).$promise;
    return DashAppointment.get({ dashboardId, action, listingId, date })
      .$promise;
  }

  function getDashAppointmentBookingInfo(dashboardId, listingId, options = {}) {
    return DashAppointmentBookingInfo.get({
      dashboardId,
      listingId,
      ...options,
    }).$promise;
  }

  function saveDashAppointment(appointment) {
    return DashAppointment.save(appointment).$promise;
  }

  // /////////////////
  // Time Functions //
  // ////////////////

  function isTimePassed(time, interval) {
    const minutes = Math.round(moment().minutes() / interval) * interval;
    return time.unix() < moment().minutes(minutes).startOf('minute').unix();
  }

  // Check if DAY is in the past; excludes time
  function isDayInPast(date) {
    const today = new Date();
    return (
      date <= today &&
      !(
        today.getDate() == date.getDate() &&
        today.getYear() == date.getYear() &&
        today.getMonth() == date.getMonth()
      )
    );
  }

  function getLastPhoneNumber(userId, type) {
    return PhoneNumber.get({ type, userId }).$promise;
  }

  function approveNonAgentForAppointment(nonAgentApprovalHash) {
    return NonAgentApproval.save({ nonAgentApprovalHash }).$promise;
  }

  return {
    getAppointmentInfo,
    getAppointmentBookingInfo,
    getAppointments,
    getAppointmentAndListingForCoop,
    getExternalConfig,
    getOfferDetails,
    recordAppointment,
    saveAppointment,
    saveNonAgent,
    deleteNonAgent,
    saveCustomAgent,
    getCustomAgent,
    deleteCustomAgent,
    changeTime,
    changeTimeExternal,
    getAppointmentDetails,
    getAppointmentConfigContact,
    getNonAgentDetails,
    isDayInPast,
    isTimePassed,
    getRetsAppointmentInfo,
    getRetsAppointmentBookingInfo,
    getExtOfferDetails,
    getRetsConfig,
    saveRetsAppointment,
    queryAppointmentEmailDelivery,
    queryAppointmentTextDelivery,
    queryAppointmentPushNotificationDelivery,
    getDashAppointmentInfo,
    getDashAppointmentBookingInfo,
    saveDashAppointment,
    getLastPhoneNumber,
    approveNonAgentForAppointment,
  };
}

export {};
